import type {InteractionHandlers} from '@gaia/utils/gaia/useInteractionHandlers';
import type {EventData, MapboxEvent} from 'mapbox-gl';
import {useEffect} from 'react';

interface StableMapEventHandlers {
    onIdle: (event: MapboxEvent) => void;
    onMoveEnd: (event: MapboxEvent) => void;
    onStyleImageMissing: (event: EventData) => void;
    onError: (event: ErrorEvent) => void;
}

export function useMapStableEvents(map: MapboxMap | undefined, handlers: StableMapEventHandlers) {
    useEffect(() => {
        if (!map) return;

        map.on('idle', handlers.onIdle);
        map.on('moveend', handlers.onMoveEnd);
        map.on('styleimagemissing', handlers.onStyleImageMissing);
        map.on('error', handlers.onError);

        return () => {
            map.off('idle', handlers.onIdle);
            map.off('moveend', handlers.onMoveEnd);
            map.off('styleimagemissing', handlers.onStyleImageMissing);
            map.off('error', handlers.onError);
        };
    }, [map, handlers]);
}

export function useMapInteractionEvents(map: MapboxMap | undefined, handlers: InteractionHandlers) {
    // `interactionHandlers` is a semi-stable ref that only changes infrequently.
    useEffect(() => {
        if (!map) return;

        map.on('click', handlers.onClick);
        map.on('dblclick', handlers.onDblClick);
        map.on('mousedown', handlers.onMouseDown);
        map.on('mousemove', handlers.onMouseMove);
        map.on('mouseup', handlers.onMouseUp);
        map.on('mouseout', handlers.onMouseOut);
        map.on('touchend', handlers.onTouchEnd);
        map.on('touchmove', handlers.onTouchMove);
        map.on('touchstart', handlers.onTouchStart);

        return () => {
            map.off('click', handlers.onClick);
            map.off('dblclick', handlers.onDblClick);
            map.off('mousedown', handlers.onMouseDown);
            map.off('mousemove', handlers.onMouseMove);
            map.off('mouseup', handlers.onMouseUp);
            map.off('mouseout', handlers.onMouseOut);
            map.off('touchend', handlers.onTouchEnd);
            map.off('touchmove', handlers.onTouchMove);
            map.off('touchstart', handlers.onTouchStart);
        };
    }, [map, handlers]);
}
